import React, { useCallback, useEffect, useState } from 'react';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import {
  Button,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';
import JourneyFormLayout from '../components/JourneyFormLayout';
import {
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  previewAnnouncement,
} from '../services/announcement';

const audienceOptions = [
  { value: 'all', label: 'All Users' },
  { value: 'tutors', label: 'Tutors' },
  { value: 'students', label: 'Students' },
  { value: 'parents', label: 'Parents' },
];
const formSteps = ['Basics', 'Audience', 'Message'];

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const isExpiredAnnouncement = (item) => {
  if (!item?.expiresAt) return false;
  const expiresAt = new Date(item.expiresAt);
  return !Number.isNaN(expiresAt.getTime()) && expiresAt <= new Date();
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
const emptyAnnouncementForm = {
  title: '',
  body: '',
  target: 'all',
  pinned: false,
  expiresAt: '',
  previewEmail: '',
  links: [{ label: '', url: '' }],
  youtubeUrl: '',
};

const cleanLinks = (links) => (Array.isArray(links) ? links : [])
  .map((entry) => ({
    label: String(entry?.label || '').trim(),
    url: String(entry?.url || '').trim(),
  }))
  .filter((entry) => entry.url);

const getYoutubeEmbedUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    let id = '';
    if (host === 'youtu.be') id = parsed.pathname.split('/').filter(Boolean)[0] || '';
    if (['youtube.com', 'm.youtube.com'].includes(host)) {
      if (parsed.pathname === '/watch') id = parsed.searchParams.get('v') || '';
      const parts = parsed.pathname.split('/').filter(Boolean);
      if (['embed', 'shorts', 'live'].includes(parts[0])) id = parts[1] || id;
    }
    return id ? `https://www.youtube.com/embed/${id}` : '';
  } catch {
    return '';
  }
};

const YouTubeEmbed = ({ announcement, url }) => {
  const embedUrl = announcement?.youtubeEmbedUrl || getYoutubeEmbedUrl(url || announcement?.youtubeUrl);
  const watchUrl = url || announcement?.youtubeUrl;
  if (!embedUrl) return null;
  return (
    <Stack spacing={1.25} sx={{ mt: 2 }}>
      <Box
        component="iframe"
        src={embedUrl}
        title="Announcement YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sx={{
          width: '100%',
          aspectRatio: '16 / 9',
          border: 0,
          borderRadius: 2,
          bgcolor: 'grey.100',
        }}
      />
      {watchUrl ? (
        <Button
          variant="text"
          size="small"
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
        >
          Watch video on YouTube
        </Button>
      ) : null}
    </Stack>
  );
};

const AdminAnnouncements = () => {
  const [form, setForm] = useState(emptyAnnouncementForm);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewSubmitting, setPreviewSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [includeExpired, setIncludeExpired] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAnnouncements({ scope: 'admin', includeExpired });
      setItems(res?.items || []);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        setItems([]);
      } else {
        toast.error(error?.response?.data?.error || 'Failed to load announcements');
      }
    } finally {
      setLoading(false);
    }
  }, [includeExpired]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (field) => (event) => {
    const value = field === 'pinned' ? event.target.checked : event.target.value;
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleLinkChange = (index, field) => (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      links: previous.links.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
    }));
  };

  const addLink = () => {
    setForm((previous) => ({ ...previous, links: [...previous.links, { label: '', url: '' }] }));
  };

  const removeLink = (index) => {
    setForm((previous) => ({
      ...previous,
      links: previous.links.filter((_, i) => i !== index).length
        ? previous.links.filter((_, i) => i !== index)
        : [{ label: '', url: '' }],
    }));
  };

  const handleSubmit = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSubmitting(true);
    try {
      await createAnnouncement({
        ...form,
        title: form.title.trim(),
        body: form.body.trim(),
        expiresAt: form.expiresAt || undefined,
        links: cleanLinks(form.links),
        youtubeUrl: form.youtubeUrl.trim(),
      });
      toast.success('Announcement posted');
      setForm(emptyAnnouncementForm);
      setActiveStep(0);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to post announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const validateStep = (step) => {
    if (step === 0 && !form.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (step === 2 && !form.body.trim()) {
      toast.error('Message is required');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, formSteps.length - 1));
  };

  const handleBackStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handlePreview = async () => {
    const title = form.title.trim();
    const body = form.body.trim();
    const previewEmail = form.previewEmail.trim();

    if (!title || !body) {
      toast.error('Title and message are required before sending a preview');
      return;
    }
    if (!previewEmail) {
      toast.error('Preview recipient email is required');
      return;
    }
    if (!isValidEmail(previewEmail)) {
      toast.error('Enter a valid preview recipient email');
      return;
    }

    setPreviewSubmitting(true);
    try {
      const result = await previewAnnouncement({
        title,
        body,
        target: form.target,
        previewEmail,
        expiresAt: form.expiresAt || undefined,
        links: cleanLinks(form.links),
        youtubeUrl: form.youtubeUrl.trim(),
      });
      const queued = result?.sendResult?.queued;
      toast.success(queued ? `Preview email queued for ${previewEmail}` : `Preview email sent to ${previewEmail}`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to send preview email');
    } finally {
      setPreviewSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAnnouncement(id);
      toast.success('Announcement deleted');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to delete announcement');
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <JourneyFormLayout
              title="Compose Announcement"
              subtitle="Guide admins through a consistent posting flow."
              steps={formSteps}
              activeStep={activeStep}
              onBack={handleBackStep}
              onNext={handleNextStep}
              onSubmit={handleSubmit}
              submitLabel="Post Announcement"
              submitting={submitting}
              disableSubmit={!form.title.trim() || !form.body.trim()}
              compact
            >
              <Stack spacing={3}>
                {activeStep === 0 && (
                  <TextField
                    label="Title"
                    value={form.title}
                    onChange={handleChange('title')}
                    fullWidth
                    required
                  />
                )}

                {activeStep === 1 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="announcement-target-label">Audience</InputLabel>
                        <Select
                          labelId="announcement-target-label"
                          label="Audience"
                          value={form.target}
                          onChange={handleChange('target')}
                        >
                          {audienceOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Expires At (optional)"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={form.expiresAt}
                        onChange={handleChange('expiresAt')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={<Switch checked={form.pinned} onChange={handleChange('pinned')} />}
                        label="Pin to top"
                      />
                    </Grid>
                  </Grid>
                )}

                {activeStep === 2 && (
                  <Stack spacing={2.5}>
                    <TextField
                      label="Message"
                      value={form.body}
                      onChange={handleChange('body')}
                      fullWidth
                      required
                      multiline
                      minRows={6}
                    />
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        Links and video
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add structured links or a YouTube video so portals and emails can display them clearly.
                      </Typography>
                      {form.links.map((link, index) => (
                        <Grid container spacing={1.5} key={`announcement-link-${index}`}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Link label"
                              value={link.label}
                              onChange={handleLinkChange(index, 'label')}
                              fullWidth
                              placeholder={`Link ${index + 1}`}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Link URL"
                              value={link.url}
                              onChange={handleLinkChange(index, 'url')}
                              fullWidth
                              placeholder="https://example.com"
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Button
                              type="button"
                              variant="outlined"
                              color="error"
                              onClick={() => removeLink(index)}
                              fullWidth
                              sx={{ height: 56 }}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      ))}
                      <Button type="button" variant="outlined" onClick={addLink} sx={{ alignSelf: 'flex-start', fontWeight: 800 }}>
                        Add Link
                      </Button>
                      <TextField
                        label="YouTube URL"
                        value={form.youtubeUrl}
                        onChange={handleChange('youtubeUrl')}
                        fullWidth
                        placeholder="https://www.youtube.com/watch?v=..."
                        helperText="Optional. Use a YouTube or youtu.be link."
                      />
                      <YouTubeEmbed url={form.youtubeUrl} />
                    </Stack>
                    <Divider />
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        Preview approval
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Send one test email for approval before posting. This does not create an announcement or notify the selected audience.
                      </Typography>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                        <TextField
                          label="Preview recipient email"
                          value={form.previewEmail}
                          onChange={handleChange('previewEmail')}
                          fullWidth
                          required
                          type="email"
                          error={Boolean(form.previewEmail) && !isValidEmail(form.previewEmail)}
                          helperText={
                            form.previewEmail && !isValidEmail(form.previewEmail)
                              ? 'Enter a valid email address'
                              : 'Required before sending a preview email.'
                          }
                        />
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={handlePreview}
                          disabled={previewSubmitting || !form.title.trim() || !form.body.trim() || !isValidEmail(form.previewEmail)}
                          sx={{ minWidth: 180, height: 56, fontWeight: 800 }}
                        >
                          {previewSubmitting ? 'Sending...' : 'Send Preview'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </JourneyFormLayout>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={<Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>Recent Announcements</Typography>}
            subheader={includeExpired ? 'Showing active and expired announcements.' : 'Showing active announcements only.'}
            action={
              <FormControlLabel
                control={
                  <Switch
                    checked={includeExpired}
                    onChange={(event) => setIncludeExpired(event.target.checked)}
                  />
                }
                label="Include expired"
              />
            }
          />
          {loading && <LinearProgress />}
          <CardContent>
            {items.length === 0 && !loading ? (
              <Typography variant="body2" color="text.secondary">
                No announcements yet.
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                {items.map((item) => {
                  const audienceLabel = audienceOptions.find((option) => option.value === item.target)?.label || item.target;
                  const expired = isExpiredAnnouncement(item);
                  return (
                    <Card key={item._id} variant="outlined">
                      <CardHeader
                        title={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {item.title}
                            </Typography>
                            {item.pinned && (
                              <Chip
                                size="small"
                                icon={<PushPinRoundedIcon fontSize="small" />}
                                label="Pinned"
                                color="warning"
                              />
                            )}
                            {expired && (
                              <Chip
                                size="small"
                                label="Expired"
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        }
                        subheader={`${formatDateTime(item.createdAt)} - ${audienceLabel}`}
                        action={
                          <IconButton aria-label="delete" onClick={() => handleDelete(item._id)}>
                            <DeleteRoundedIcon />
                          </IconButton>
                        }
                      />
                      <Divider />
                      <CardContent>
                        <Typography variant="body2" color="text.primary">
                          {item.body}
                        </Typography>
                        {Array.isArray(item.links) && item.links.length > 0 && (
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                            {item.links.map((link, index) => (
                              <Button
                                key={`${link.url}-${index}`}
                                variant="outlined"
                                size="small"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {link.label || `Link ${index + 1}`}
                              </Button>
                            ))}
                          </Stack>
                        )}
                        {item.youtubeUrl && <YouTubeEmbed announcement={item} />}
                      </CardContent>
                      {(item.expiresAt || item.updatedAt) && (
                        <CardActions
                          sx={{
                            justifyContent: item.expiresAt ? 'space-between' : 'flex-end',
                            px: 2,
                          }}
                        >
                          {item.expiresAt && (
                            <Chip
                              size="small"
                              color={expired ? 'error' : 'default'}
                              variant={expired ? 'outlined' : 'filled'}
                              label={`${expired ? 'Expired' : 'Expires'} ${formatDateTime(item.expiresAt)}`}
                            />
                          )}
                          {item.updatedAt && item.updatedAt !== item.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                              Updated {formatDateTime(item.updatedAt)}
                            </Typography>
                          )}
                        </CardActions>
                      )}
                    </Card>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>
    </DashboardLayout>
  );
};

export default AdminAnnouncements;
